import Axios from '@/utils/axios';
import { ModelListRequest, ModelListResponse } from '@/types/common';
import { getBackendErrorMessage } from '@/utils/errorHandler';

export interface CreateModelRequest {
  oemID: string;
  name: string;
  unitType: 'NEW' | 'SECOND';
  purchasePrice: number;
  sellingPrice: number;
  contractType: 'RTO' | 'RENT';
  batteryType: 'CHARGING' | 'SWAP' | 'BOTH';
  maxDistancePerBattery: number;
  maxWeight: number;
  deposit: number;
  area: string;
  // Temp-bucket URL from the upload service; the BE relocates it into the
  // model's own folder. Omit when no photo was picked.
  imageURL?: string | null;
  createdBy: string;
}

export interface UpdateModelRequest {
  oemID: string;
  name: string;
  unitType: 'NEW' | 'SECOND';
  purchasePrice: number;
  sellingPrice: number;
  contractType: 'RTO' | 'RENT';
  batteryType: 'CHARGING' | 'SWAP' | 'BOTH';
  maxDistancePerBattery: number;
  maxWeight: number;
  deposit: number;
  area: string;
  // Unchanged URL ⇒ photo kept as-is, `null` ⇒ photo removed, new temp URL ⇒
  // photo replaced.
  imageURL?: string | null;
  updatedBy: string;
}

export interface AreaResponse {
  areas: string[];
}

export const getListModel = async (
  params?: ModelListRequest
): Promise<ModelListResponse> => {
  try {
    const response = await Axios.get('/v1/models', { params });
    return response.data;
  } catch (error: any) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export const createModel = async (data: CreateModelRequest): Promise<any> => {
  try {
    const response = await Axios.post('/v1/models', data);
    return response.data;
  } catch (error: any) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export const updateModel = async (
  id: string,
  data: UpdateModelRequest
): Promise<any> => {
  try {
    const response = await Axios.patch(`/v1/models/${id}`, data);
    return response.data;
  } catch (error: any) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export const getAreas = async (
  params?: Record<string, any>
): Promise<AreaResponse> => {
  try {
    const response = await Axios.get('/v1/models/areas', { params });
    return response.data.data;
  } catch (error: any) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export const downloadModelsCsv = async (
  params?: ModelListRequest
): Promise<Blob> => {
  try {
    const response = await Axios.get('/v1/models/download/csv', {
      params,
      responseType: 'blob',
    });
    return response.data;
  } catch (error: any) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};
